import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import type { MenuItem } from "primereact/menuitem";
import { Steps } from "primereact/steps";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Clinic } from "src/core/backend/clients/backendClient";
import { useDate } from "src/core/hooks/useDate";
import { logService } from "src/core/services/logService";
import { textService } from "src/core/services/textService";
import { viewService } from "src/core/services/viewService";
import { useAuthStore } from "src/features/auth/services/useAuthStore";
import { ClinicModel } from "src/features/clinics/models/clinicModel";
import { DoctorProfileModel } from "src/features/clinics/models/doctorProfileModel";
import { useClinicsStore } from "src/features/clinics/services/useClinicsStore";
import { ParticipantModel } from "src/features/participant/models/participantModel";
import { useSettingsStore } from "src/features/settings/services/useSettingsStore";
import { useAppointmentStore } from "../../services/useAppointmentStore";
import "./AppointmentPage.css";
import { AppointmentStep1 } from "./components/AppointmentStep1/AppointmentStep1";
import { AppointmentStep2 } from "./components/AppointmentStep2/AppointmentStep2";
import { AppointmentStep3 } from "./components/AppointmentStep3/AppointmentStep3";
import { AppointmentStep4 } from "./components/AppointmentStep4/AppointmentStep4";
import { AppointmentStep5 } from "./components/AppointmentStep5/AppointmentStep5";

export type AppointmentStepProps = {
  activeStepIndex: number;
  setActiveStepIndex: (step: number) => void;
  previousStepIndex: number;
};

export function AppointmentPage() {
  const stepsRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { dateTimeFormat } = useDate();

  const authStore = useAuthStore();
  const clinicsStore = useClinicsStore();
  const appointmentStore = useAppointmentStore();
  const settingsStore = useSettingsStore();

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [previousIndex, setPreviousIndex] = useState<number>(0);

  const [urlProfileId, setUrlProfileId] = useState<number>(0);

  const steps: MenuItem[] = [
    {
      label: t("Home.Appointments.Step1Title"),
      data: appointmentStore.selectedPerson?.name ?? "",
      className: "appointmentStep1",
      template: (item) => stepsItemRenderer(item, 0),
    },
    {
      label: t("Home.Appointments.Step2Title"),
      data: appointmentStore.selectedClinic?.name ?? "",
      className: "appointmentStep2",
      template: (item) => stepsItemRenderer(item, 1),
    },
    {
      label: t("Home.Appointments.Step3Title"),
      data: appointmentStore.selectedDoctorProfile?.doctorName ?? "",
      className: "appointmentStep3",
      template: (item) => stepsItemRenderer(item, 2),
    },
    {
      label: t("Home.Appointments.Step4Title"),
      data: appointmentStore.selectedDateTime
        ? textService.formatDate(
            appointmentStore.selectedDateTime,
            dateTimeFormat
          )
        : "",
      className: "appointmentStep4",
      template: (item) => stepsItemRenderer(item, 3),
    },
    {
      label: t("Home.Appointments.Step5Title"),
      className: "appointmentStep5",
      template: (item) => stepsItemRenderer(item, 4),
    },
  ];

  useEffect(() => {
    clinicsStore.clinicsList();

    setPreviousIndex(-1);
  }, []);

  useEffect(() => {
    setPreviousIndex(activeIndex);

    if (viewService.isMobileSize) {
      setTimeout(() => {
        const activeStep = stepsRef.current?.querySelector(
          ".p-steps-item.p-highlight .active"
        );
        if (activeStep) {
          activeStep.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          });
        }
      }, 100);
    }
  }, [activeIndex]);

  useEffect(() => {
    if (logService.globalLog)
      logService.log("AppointmentPage clinics", clinicsStore.clinics);

    if (clinicsStore.clinics.length > 0 && urlProfileId > 0) {
      processUrlProfileId(clinicsStore.clinics, urlProfileId);
    }
  }, [clinicsStore.clinics, urlProfileId]);

  useEffect(() => {
    if ((settingsStore?.errorList?.errorCodes?.length ?? 0) === 0) return;

    const queryParams = new URLSearchParams(location.search);

    if (logService.globalLog)
      logService.log("AppointmentPage queryParams", queryParams);

    setUrlProfileId(Number.parseInt(queryParams.get("pid") ?? "0"));

    if (queryParams.get("step") === "5") setActiveIndex(4);
  }, [settingsStore.errorList]);

  const processUrlProfileId = (clinics: Clinic[], urlProfileId: number) => {
    const profile = clinics
      .flatMap((clinic) => clinic.doctors)
      .flatMap((doctor) => doctor?.profiles)
      ?.find((profile) => profile?.id === urlProfileId);
    if (!profile) return;

    const clinic = clinics.find((clinic) => clinic.id === profile.clinicId);
    if (!clinic) return;

    const doctor = clinic.doctors?.find(
      (doctor) => doctor.id === profile.doctorId
    );
    if (!doctor) return;

    appointmentStore.setSelectedPerson(
      new ParticipantModel({
        id: 0,
        userId: 0,
        name: t("Home.Appointments.ForMyself"),
        phone: authStore.user?.phone,
      })
    );

    appointmentStore.setSelectedClinic(
      new ClinicModel({
        id: clinic.id,
        name: clinic.name,
      })
    );

    appointmentStore.setSelectedDoctorProfile(
      new DoctorProfileModel({
        doctorId: doctor.id,
        doctorName: doctor.name,
        profileId: profile.id,
        profileName: profile.name,
      })
    );

    appointmentStore.setQuickAppointment(true);

    setActiveIndex(3); // Set to Step 4 directly if profileId is provided
  };

  const onStepClick = (index: number) => {
    let newIndex = index;
    if (newIndex > activeIndex) newIndex = activeIndex;
    setActiveIndex(newIndex);
  };

  const stepsItemRenderer = (item: MenuItem, itemIndex: number) => {
    const isActiveItem = activeIndex === itemIndex;
    const backgroundColor = isActiveItem
      ? "var(--primary-color)"
      : "var(--surface-b)";
    const textColor = isActiveItem
      ? "var(--primary-color-text)"
      : "var(--text-color-secondary)";

    item.icon = activeIndex > itemIndex ? "pi pi-check-circle" : item.icon;

    return (
      <div className="flex flex-column align-items-center gap-1 mt-1">
        <span
          className={
            "flex align-items-center justify-content-center align-items-center border-circle h-2rem w-2rem z-1 " +
            (activeIndex >= itemIndex ? "cursor-pointer" : "cursor-not-allowed")
          }
          style={{
            backgroundColor: backgroundColor,
            color: textColor,
            border: (isActiveItem ? "3px " : "1px ") + "solid #e9ecef",
          }}
          onClick={() => onStepClick(itemIndex)}
        >
          {!item.icon && <span className="font-bold">{itemIndex + 1}</span>}
          {item.icon && (
            <i
              className={item.icon + " font-bold"}
              style={{ color: "var(--green-500)" }}
            />
          )}
        </span>

        {!item.data && (
          <span
            className={
              "text-center" + (isActiveItem ? " font-bold active" : "")
            }
            style={{ margin: "0 0.5rem" }}
          >
            {item.label}
          </span>
        )}
        {item.data && (
          <Chip
            className={
              "font-italic font-medium " +
              (activeIndex >= itemIndex
                ? "cursor-pointer"
                : "cursor-not-allowed")
            }
            label={item.data}
            onClick={() => onStepClick(itemIndex)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="appointmentPage">
      <Card
        title={t("Home.Appointments.Title")}
        className="card"
      >
        <div
          ref={stepsRef}
          className={"flex flex-column"}
        >
          <Steps
            className="mb-1 overflow-auto"
            model={steps}
            activeIndex={activeIndex}
            readOnly={false}
          />

          <div className="step-content">
            {activeIndex === 0 && (
              <AppointmentStep1
                activeStepIndex={activeIndex}
                setActiveStepIndex={setActiveIndex}
                previousStepIndex={previousIndex}
              />
            )}
            {activeIndex === 1 && (
              <AppointmentStep2
                activeStepIndex={activeIndex}
                setActiveStepIndex={setActiveIndex}
                previousStepIndex={previousIndex}
              />
            )}
            {activeIndex === 2 && (
              <AppointmentStep3
                activeStepIndex={activeIndex}
                setActiveStepIndex={setActiveIndex}
                previousStepIndex={previousIndex}
              />
            )}
            {activeIndex === 3 && (
              <AppointmentStep4
                activeStepIndex={activeIndex}
                setActiveStepIndex={setActiveIndex}
                previousStepIndex={previousIndex}
              />
            )}
            {activeIndex === 4 && (
              <AppointmentStep5
                activeStepIndex={activeIndex}
                setActiveStepIndex={setActiveIndex}
                previousStepIndex={previousIndex}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
